import { useResendEscriptSignupInvite } from '@/hooks/auth/useResendEscriptSignupInvite';
import { usePreOnboardingContext } from '@/hooks/escript/usePreOnboardingContext';
import { emitUIEvent } from '@montugroup/data-collection';
import { Link, toast } from '@montugroup/design-system';
import { styled } from '@mui/material/styles';
import { useState } from 'react';

type StyledLinkProps = {
  disabled: boolean;
};

const StyledLink = styled(Link, { shouldForwardProp: (prop) => prop !== 'disabled' })<StyledLinkProps>(
  ({ disabled }) => ({
    cursor: 'pointer',
    ...(!disabled && {
      '&&': {
        // workaround to increase specificity instead of using '!important'
        textDecoration: 'underline',
      },
    }),
  }),
);

export const ResendInviteButton = () => {
  const [isCounting, setIsCounting] = useState<boolean>(false);
  const [count, setCount] = useState<number>(10);
  const { customerDetails, setAlert } = usePreOnboardingContext();
  const { mutate, isPending: isResendPending } = useResendEscriptSignupInvite();

  if (!customerDetails) {
    return null;
  }

  // todo: emitUIEvent is an interim approach until the data collection package is updated with 'Data Events' schema
  const handleResendClick = () => {
    setIsCounting(true);
    mutate(
      { body: { data: { id: customerDetails.preOnboardUserId } } },
      {
        onSuccess: () => {
          setAlert({
            show: true,
            severity: 'success',
            title: '',
            content: 'Invite link sent.',
          });
          emitUIEvent(
            {
              component_name: 'resend_invite_button',
              event_name: 'success',
              id: 'escript_onboarding_token_success',
              value: 'Invite link sent success',
            },
            [],
          );
        },
        onError: () => {
          toast.warn('There was an error. Please try again soon.');
          emitUIEvent(
            {
              component_name: 'resend_invite_button',
              event_name: 'error',
              id: 'escript_onboarding_token_error',
              value: 'Invite link sent error',
            },
            [],
          );
        },
      },
    );
    const countdownTimer = setInterval(() => {
      setCount((prevCount) => {
        if (prevCount <= 1) {
          clearInterval(countdownTimer);
          setIsCounting(false);
          return 10; // Reset
        }
        return prevCount - 1;
      });
    }, 1000);
  };

  if (isCounting) {
    return <>Resend in {count.toString()}</>;
  }

  return (
    <StyledLink
      disabled={isResendPending}
      onClick={handleResendClick}
      data-tracking-id="escript_onboarding_resend_invite"
    >
      Resend invite
    </StyledLink>
  );
};
